<template>
  <div v-if="authorized">
    <b-form @submit="onSubmit">
      <KTPortlet :title="title">
        <template v-slot:body>
          <b-form-group
            label="Acquirer Id"
            label-for="acquirerId"
            class="acquirer-container"
          >
            <b-form-input
              id="acquirerId"
              placeholder=""
              required
              :readonly="acquirerIdReadOnly"
              v-model="$v.acquirer.acquirerId.$model"
              type="text"
              :state="validateState('acquirerId')"
              aria-describedby="acquirerId-error"
            />
            <b-form-invalid-feedback id="acquirerId-error">
              This field is required. Must be atleast 3 characters.
            </b-form-invalid-feedback>
          </b-form-group>
          <b-form-group
            label="Acquirer Name"
            label-for="name"
            class="acquirer-container"
          >
            <b-form-input
              id="name"
              placeholder=""
              required
              v-model="$v.acquirer.name.$model"
              type="text"
              :state="validateState('name')"
              aria-describedby="name-error"
            />
            <b-form-invalid-feedback id="name-error">
              This field is required. Must be atleast 3 characters.
            </b-form-invalid-feedback>
          </b-form-group>
          <div class="accepted-types">
            <label for="card-types">Accepted Card Types</label>
            <v-select
              name="card-types"
              v-model="$v.acquirer.cardTypes.$model"
              :items="cardTypeList"
              item-text="longName"
              multiple
              clearable
              chips
              deletable-chips
              return-object
              :success="validateState('cardTypes')"
              :error-messages="cardTypeError"
            ></v-select>
          </div>
        </template>
        <template v-slot:foot>
          <b-row class="d-flex justify-content-between">
            <b-button type="submit" variant="primary">Save</b-button>
            <b-button type="button" @click="cancel" variant="secondary"
              >Cancel</b-button
            >
          </b-row>
        </template>
      </KTPortlet>
    </b-form>
    <LoadingModal v-bind="loadingModal" />
    <AlertModal
      v-bind="alertModal"
      v-model="alertModal.show"
      @dismiss="resetAlert"
    />
    <ConfirmationModal
      v-bind="confirmationModal"
      v-model="confirmationModal.show"
      @dismiss="resetConfirmation"
    />
  </div>
</template>

<script>
import KTPortlet from "@/views/partials/content/Portlet";
import { API, graphqlOperation } from "aws-amplify";
import AlertModalMixin from "@/components/widgets/AlertModalMixin.js";
import { validationMixin } from "vuelidate";
import { required, minLength } from "vuelidate/lib/validators";

const AlertModal = AlertModalMixin.AlertModal;
const LoadingModal = AlertModalMixin.LoadingModal;
const ConfirmationModal = AlertModalMixin.ConfirmationModal;

const DEFAULT_CARD_TYPES_SEARCH_LIMIT = 50;

export default {
  mixins: [AlertModalMixin, validationMixin],
  components: {
    KTPortlet
  },
  props: {
    item: {
      type: Object,
      required: false,
      default() {
        return null;
      }
    }
  },
  data() {
    return {
      acquirerIdReadOnly: false,
      createNewRecord: true,
      acquirer: {
        acquirerId: "",
        name: "",
        cardTypes: []
      },
      cardTypeList: []
    };
  },
  validations: {
    acquirer: {
      acquirerId: {
        required,
        minLength: minLength(3)
      },
      name: {
        required,
        minLength: minLength(3)
      },
      cardTypes: {
        required,
        minLength: minLength(1)
      }
    }
  },
  computed: {
    submitMethod() {
      return this.createNewRecord ? this.createAquirer : this.updateAquirer;
    },
    title() {
      return this.createNewRecord
        ? "New Acquirer"
        : `${this.item.acquirerId} - ${this.item.name}`;
    },
    loadingMessage() {
      return this.createNewRecord ? "Creating" : "Updating";
    },
    authorized() {
      return this.$role.verify([this.$role.ADMIN]);
    },
    cardTypeError() {
      const result = this.validateState("cardTypes");
      if (result == null) return "";

      return result ? "" : "Must have atleast 1 accepted card type";
    }
  },
  methods: {
    validateState(name) {
      const { $dirty, $error } = this.$v.acquirer[name];
      return $dirty ? !$error : null;
    },
    async setupExistingRecord(existingRecord) {
      this.acquirerIdReadOnly = true;
      Object.assign(this.acquirer, existingRecord);
      this.acquirer.cardTypes = await this.getAquirerCardTypes(
        this.acquirer.acquirerId
      );
    },
    async setupComponent(item) {
      if (!!item) {
        await this.setupExistingRecord(item);
        this.createNewRecord = false;
      } else {
        this.createNewRecord = true;
      }
      this.cardTypeList = await this.getCardTypes();
    },
    async getAquirerCardTypes(id) {
      const getAcquirerCardTypes = `
              query GetAcquirerCardTypes($acquirerId: String!) {
                  getAcquirerCardTypes(acquirerId: $acquirerId) {
                    name
                    longName
                  }
              }
            `;

      try {
        const getAcquirerCardTypesResponse = await API.graphql({
          ...graphqlOperation(getAcquirerCardTypes, { acquirerId: id })
        });

        return getAcquirerCardTypesResponse.data.getAcquirerCardTypes;
      } catch (e) {
        this.$logger.error("Error getting acquirer card types");
        this.$logger.error(e);
        await this.alert(
          "An error occured while retrieving the card types for this acquirer",
          AlertModal.ERROR
        );
        this.close();
      }
    },
    async getCardTypes() {
      let errorMessage = "";
      const getCardTypes = `
              query GetCardTypes($filter: Filter) {
                  getCardTypes(filter: $filter) {
                    cardTypeList{
                      cardName
                      shortCardName
                    }
                    nextToken
                    statusMessage
                  }
              }
            `;
      try {
        let nextToken = null;
        const limit = DEFAULT_CARD_TYPES_SEARCH_LIMIT;
        let cardTypes = [];

        do {
          const filter = {
            limit,
            nextToken
          };

          const getCardTypesResponse = await API.graphql({
            ...graphqlOperation(getCardTypes, { filter })
          });
          const list = getCardTypesResponse.data.getCardTypes.cardTypeList;
          if (list.length < 1) {
            const statusMessage =
              getCardTypesResponse.data.getCardTypes.statusMessage;
            errorMessage += statusMessage;
            throw new Error(statusMessage);
          }

          cardTypes = cardTypes.concat(
            list.map(c => {
              return {
                name: c.shortCardName,
                longName: c.cardName
              };
            })
          );

          nextToken = getCardTypesResponse.data.getCardTypes.nextToken;
        } while (nextToken);
        return cardTypes;
      } catch (e) {
        this.$logger.error("Error getting the list of card types");
        this.$logger.error(e);
        await this.alert(
          `An error occured while retrieving the list of card types. ${errorMessage}`,
          AlertModal.ERROR
        );
        this.close();
      }
    },
    async onSubmit(e) {
      e.preventDefault();
      this.$v.acquirer.$touch();
      if (this.$v.acquirer.$anyError) {
        this.alert("Form invalid", AlertModal.WARNING);
        return;
      }
      this.startLoadingDialog(`${this.loadingMessage} Acquirer`);
      const result = await this.submitMethod(this.acquirer);
      if (result.success) {
        await this.stopLoadingDialog(
          `Operation Successful`,
          AlertModal.SUCCESS
        );
        this.close(this.acquirer);
      } else {
        this.stopLoadingDialog(result.errorMessage, AlertModal.ERROR);
      }
    },
    async createAquirer(acquirer) {
      let success = false;
      let errorMessage = `An error occured while creating Acquirer: ${acquirer.acquirerId}.`;
      const createAcquirer = `
            mutation CreateAcquirer(
              $body: CreateAcquirerInput!
            ) {
              createAcquirer(body: $body) {
                success
  	            errorMessage
              }
            }
          `;

      const acquirerInput = {
        acquirerId: acquirer.acquirerId,
        name: acquirer.name,
        cardTypes: acquirer.cardTypes
      };

      try {
        const createAcquirerResponse = await API.graphql({
          ...graphqlOperation(createAcquirer, { body: acquirerInput })
        });

        success = createAcquirerResponse.data.createAcquirer.success;
        errorMessage = createAcquirerResponse.data.createAcquirer.errorMessage;
      } catch (e) {
        this.$logger.error("Error in create acquirer method");
        this.$logger.error(e);
      }
      return { success, errorMessage };
    },
    async updateAquirer(acquirer) {
      let success = false;
      let errorMessage = `An error occured while updating Acquirer: ${acquirer.acquirerId}.`;
      const updateAcquirer = `
            mutation UpdateAcquirer(
              $body: UpdateAcquirerInput!
            ) {
              updateAcquirer(body: $body) {
                success
  	            errorMessage
              }
            }
          `;

      const acquirerInput = {
        acquirerId: acquirer.acquirerId,
        name: acquirer.name,
        cardTypes: acquirer.cardTypes
      };

      try {
        const updateAcquirerResponse = await API.graphql({
          ...graphqlOperation(updateAcquirer, { body: acquirerInput })
        });

        success = updateAcquirerResponse.data.updateAcquirer.success;
        errorMessage = updateAcquirerResponse.data.updateAcquirer.errorMessage;
      } catch (e) {
        this.$logger.error("Error in update acquirer method");
        this.$logger.error(e);
      }
      return { success, errorMessage };
    },
    cancel() {
      this.close();
    },
    close(savedItem) {
      this.$emit("close", savedItem);
    }
  },
  async created() {
    try {
      await this.setupComponent(this.item);
    } catch (e) {
      this.$logger.error("Error setting up component");
      this.$logger.error(e);
    }
  }
};
</script>

<style lang="scss" scoped>
* {
  font-family: "Open Sans", sans-serif;
}
.acquirer-container > label,
.accepted-types > label {
  font-weight: 700;
  font-size: 14px;
  color: black;
}

.acquirer-container > div > input {
  box-sizing: border-box;
  min-height: 40.5px;
  min-width: 100%;
  border: 0.5px solid #d8d8e4;
  border-radius: 4px;
  background-color: #ffffff;
  padding: 1px 10px 1px 10px;
}

.accepted-types > .v-select > .v-input__control > .v-input__slot:before {
  border-style: none !important;
}
.accepted-types > .v-select > .v-input__control > .v-input__slot:after {
  border-style: none !important;
}

.accepted-types
  > .v-select
  > .v-input__control
  > .v-input__slot
  > .v-select__slot {
  box-sizing: border-box;
  max-height: 40.5px;
  min-height: 40.5px;
  min-width: 100%;
  border: 0.5px solid #d8d8e4;
  border-radius: 4px;
  background-color: #ffffff;
  padding: 1px 10px 1px 10px;
}

.accepted-types {
  display: flex;
  flex-direction: column;
}
</style>
